import React from 'react';
import { ICellRendererParams, ICellEditorParams } from 'ag-grid-community';
import Select, { MultiValue } from 'react-select/creatable';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover/popover';
import { i18n } from "../../lib/i18n";

interface Service {
  name: string;
  category: string;
}

interface SelectOption {
  value: string;
  label: string;
  service: Service;
}

interface ServiceRendererProps {
  value: Service[];
  services: Service[];
}

export const ServiceRenderer = (props: ServiceRendererProps) => {
  if (!props.value || !Array.isArray(props.value)) return <span></span>;
  
  return (
    <span className="resource-import-edit-grid__services">
      {props.value.map(s => `${s.category}:${s.name}`).join(', ')}
    </span>
  );
};

interface ServiceEditorProps extends ICellEditorParams {
  value: Service[];
  services: Service[];
  onValueChange(value: Service[]): void;
  stopEditing(): void;
}

export const ServiceEditor = (props: ServiceEditorProps) => {
  const [selectedServices, setSelectedServices] = React.useState<Service[]>(
    Array.isArray(props.value) ? props.value : []
  );

  // Convert services to react-select options format
  const options = props.services.map(service => ({
    value: `${service.category}:${service.name}`,
    label: `${service.category}:${service.name}`,
    service: service
  }));

  // Convert current value to react-select format
  const value = selectedServices.map(service => ({
    value: `${service.category}:${service.name}`,
    label: `${service.category}:${service.name}`,
    service: service
  }));

  const handleChange = (newValue: MultiValue<SelectOption>) => {
    setSelectedServices(newValue.map(v => v.service));
  };

  const handleCreate = (inputValue: string) => {
    const parts = inputValue.split(':');
    if (parts.length !== 2) {
      return; // Invalid format
    }

    const [category, name] = parts.map(p => p.trim());
    if (!category || !name) {
      return; // Empty category or name
    }

    const newService = { category, name };
    setSelectedServices([...selectedServices, newService]);
  };

  return (
    <Popover defaultOpen onOpenChange={() => {
      props.onValueChange(selectedServices);
      props.stopEditing();
    }}>
      <PopoverTrigger>
        <ServiceRenderer 
          value={selectedServices} 
          services={props.services}
        />
      </PopoverTrigger>
      <PopoverContent className="bg-white p-4 min-w-[300px]">
        <div className="resource-import-edit-grid__services-editor">
          <Select
            isMulti
            value={value}
            onChange={handleChange}
            options={options}
            className="w-full"
            classNamePrefix="react-select"
            placeholder={i18n.t('resource_import.select_or_type_service')}
            menuPosition="fixed"
            menuPortalTarget={document.body}
            groupBy={(option) => option.service.category}
            onCreateOption={handleCreate}
            styles={{
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999
              })
            }}
            formatCreateLabel={(inputValue) => {
              const parts = inputValue.split(':');
              if (parts.length !== 2) {
                return i18n.t('resource_import.use_format_category_name');
              }
              const [category, name] = parts.map(p => p.trim());
              if (!category || !name) {
                return i18n.t('resource_import.use_format_category_name');
              }
              return i18n.t('resource_import.add_category_name', { value: `${category}:${name}` });
            }}
          />
          <div className="text-sm text-gray-500 mt-2">
            {i18n.t('resource_import.type_service_format')}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}; 
